import { useTranslation } from "@repo/i18n";
import { css } from "@repo/styled-system/css";
import { useState } from "react";
import type { CalendarExceptionResponse } from "./scheduleApi";

export interface ClosuresCalendarProps {
  exceptions: CalendarExceptionResponse[];
  /** Set a CLOSED (MVP override editing lands with the availability engine) ruling for a date. */
  onSet: (date: string, kind: "CLOSED", reason: string | null) => Promise<void>;
  onDelete: (date: string) => Promise<void>;
}

/**
 * The closures calendar (features/opening-hours-and-services.md): add a CLOSED ruling for a date behind
 * an ALWAYS-ON confirm step stating existing bookings are flagged, never cancelled (§C.4 —
 * `CalendarExceptionSet` drives booking-side flagging asynchronously). Listing shows current rulings;
 * OVERRIDE window editing is deferred to the availability-engine screens.
 */
export function ClosuresCalendar({ exceptions, onSet, onDelete }: ClosuresCalendarProps) {
  const { t } = useTranslation();
  const [date, setDate] = useState("");
  const [reason, setReason] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  async function confirmClose() {
    if (!date) return;
    setBusy(true);
    try {
      await onSet(date, "CLOSED", reason.trim() === "" ? null : reason);
      setDate("");
      setReason("");
      setConfirming(false);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className={css({ display: "flex", flexDirection: "column", gap: "3", maxW: "xl" })}>
      <h3 className={css({ fontSize: "lg", fontWeight: "semibold" })}>
        {t("schedule.closures.title")}
      </h3>

      <div className={css({ display: "flex", gap: "2", alignItems: "flex-end", flexWrap: "wrap" })}>
        <label
          className={css({ display: "flex", flexDirection: "column", gap: "1", fontSize: "sm" })}
        >
          {t("schedule.closures.date")}
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className={inputStyle}
          />
        </label>
        <label
          className={css({
            display: "flex",
            flexDirection: "column",
            gap: "1",
            fontSize: "sm",
            flex: "1",
          })}
        >
          {t("schedule.closures.reason")}
          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className={inputStyle}
          />
        </label>
        <button
          type="button"
          disabled={!date}
          onClick={() => setConfirming(true)}
          className={css({
            px: "3",
            py: "2",
            borderWidth: "1px",
            rounded: "md",
            _disabled: { opacity: "0.5" },
          })}
        >
          {t("schedule.closures.addClosed")}
        </button>
      </div>

      {confirming ? (
        <dialog
          open
          aria-label={t("schedule.closures.confirmTitle")}
          className={css({
            position: "static",
            p: "3",
            borderWidth: "1px",
            rounded: "md",
            bg: "bg.subtle",
          })}
        >
          <p className={css({ fontSize: "sm" })}>{t("schedule.closures.confirmMessage")}</p>
          <div className={css({ display: "flex", gap: "2", mt: "2" })}>
            <button
              type="button"
              disabled={busy}
              onClick={() => void confirmClose()}
              className={css({ px: "3", py: "1", borderWidth: "1px", rounded: "md" })}
            >
              {t("schedule.closures.confirm")}
            </button>
            <button
              type="button"
              onClick={() => setConfirming(false)}
              className={css({ px: "3", py: "1", borderWidth: "1px", rounded: "md" })}
            >
              {t("schedule.closures.cancel")}
            </button>
          </div>
        </dialog>
      ) : null}

      <ul
        className={css({
          display: "flex",
          flexDirection: "column",
          gap: "1",
          listStyle: "none",
          p: "0",
        })}
      >
        {exceptions.map((e) => (
          <li
            key={e.date}
            className={css({ display: "flex", gap: "3", alignItems: "center", fontSize: "sm" })}
          >
            <span className={css({ fontWeight: "medium" })}>{e.date}</span>
            <span className={css({ color: "fg.muted" })}>
              {e.kind === "CLOSED"
                ? t("schedule.closures.closed")
                : t("schedule.closures.override")}
            </span>
            {e.reason ? <span className={css({ color: "fg.muted" })}>{e.reason}</span> : null}
            <button
              type="button"
              onClick={() => void onDelete(e.date)}
              className={css({ ml: "auto", px: "2", py: "1", fontSize: "xs" })}
            >
              {t("schedule.closures.remove")}
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

const inputStyle = css({ px: "3", py: "2", borderWidth: "1px", rounded: "md" });
